import { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

// internal hooks
import { useEscape } from "../../hooks/useEscape";
import { useVerifyUnique } from "../../hooks/useVerifyUnique";

// styles and images
import Close from "../../assets/close.svg";
import Delete from "../../assets/delete.svg";
import "../rate-sheets/AddRateSheet.css";

// store
import {
  dbDataStore,
  messageAndErrorStore,
  triggersStore,
  userStore,
} from "../../store/Store";

// components
import InputField from "../../formComponents/InputField";
import FormButton from "../../formComponents/FormButton";
import Loading from "../../components/Loading";

export default function AddAssetRateSheet(props) {
  // gobal state
  const JwtToken = userStore((userDataStore) => userDataStore.JwtToken);
  const currentUser = userStore((userDataStore) => userDataStore.currentUser);
  const currentCompany = userStore(
    (userDataStore) => userDataStore.currentCompany
  );
  const typesData = dbDataStore((dbData) => dbData.typesData);
  const setAssetRateSheets = dbDataStore((dbData) => dbData.setAssetRateSheets);
  const setAssetRateSheetTrigger = triggersStore(
    (data) => data.setAssetRateSheetTrigger
  );
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const setAuthError = messageAndErrorStore((data) => data.setAuthError);
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // local
  let id = 0;
  if (props.id) {
    id = props.id;
  }
  const [counter, setCounter] = useState(0);
  const [formError, setFormError] = useState(null);
  const [typeOptions, setTypeOptions] = useState();
  const [methodOptions, setMethodOptions] = useState();
  const [assetTypeOptions, setAssetTypeOptions] = useState();
  const [low, setLow] = useState([1]);
  const [high, setHigh] = useState([10]);
  const [rates, setRates] = useState([]);
  const [isPending, setIsPending] = useState(false);
  const [oldName, setOldName] = useState("");
  const [dataPending, setDataPending] = useState(false);
  const [rateSheet, setRateSheet] = useState({
    id: 0,
    owner_company_id: currentCompany.id,
    method_id: 0,
    type_id: 0,
    asset_type_id: 0,
    type_name: "",
    asset_type_name: "",
    method_name: "",
    rs_name: "",
    created_by_id: 0,
    updated_by_id: 0,
    is_deleted: false,
    mileage_bands: 0,
    rs_rates: ["0"],
  });
  // other
  const { exists } = useVerifyUnique(
    props.docs,
    "rs_name",
    rateSheet.rs_name,
    oldName
  );
  const navigate = useNavigate();

  useEffect(() => {
    let types = [];
    let methods = [];
    let assetTypes = [];
    if (typesData) {
      setFormError(null);
      setIsPending(false);
      typesData.forEach((o) => {
        if (o.tbl_column === "type_id") {
          types.push({ value: o.id, label: o.type_name });
        } else if (o.tbl_column === "asset_method_id") {
          methods.push({ value: o.id, label: o.type_name });
        } else if (o.tbl_column === "asset_type_id") {
          assetTypes.push({ value: o.id, label: o.type_name });
        }
      });
    } else {
      setFormError("Trouble Retreiving Data");
    }
    setTypeOptions(types);
    setMethodOptions(methods);
    setAssetTypeOptions(assetTypes);
  }, [typesData, setTypeOptions, setMethodOptions, setAssetTypeOptions]);

  // get rate sheet if id exists
  useEffect(() => {
    if (props.id) {
      setDataPending(true);
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);
      const requestOptions = {
        method: "GET",
        headers: headers,
      };
      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/asset-rate-sheet/${props.id}`,
        requestOptions
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              setAuthError(res.message);
              navigate("/login");
            }
            setFormError("Trouble Retreiving Data");
            return;
          } else {
            return res.json();
          }
        })
        .then((data) => {
          setRateSheet((current) => {
            return {
              ...current,
              ...data.rate_sheet,
            };
          });
          setOldName(data.rate_sheet.rs_name);
          var count;
          if (data.rate_sheet.type_name === "Tier Rates") {
            count = data.rates.length;
            let l = [];
            let h = [];
            setCounter(count);
            data.rates.forEach((r) => {
              l.push(r.low_band);
              h.push(r.high_band);
            });
            setLow(l);
            setHigh(h);
          }
          let arr = [];
          data.rates.forEach((r) => {
            arr.push(r.rate);
          });
          setRates(arr);
          setCounter(count - 1);
          setAssetRateSheets(data.data);
          setDataPending(false);
        })
        .catch((_) => {
          setFormError("Something went wrong");
          return;
        })
        .finally(() => setDataPending(false));
      return () => controller.abort();
    }
  }, [
    props.id,
    JwtToken,
    navigate,
    setAuthError,
    setAuthStatus,
    setAssetRateSheets,
    currentCompany,
  ]);

  useEscape(
    setAssetRateSheetTrigger,
    [
      setCounter,
      setFormError,
      setRateSheet,
      setRates,
      setLow,
      setHigh,
      setCounter,
      props.setDocId,
    ],
    [
      0,
      null,
      {
        id: 0,
        owner_company_id: currentCompany.id,
        method_id: 0,
        type_id: 0,
        asset_type_id: 0,
        type_name: "",
        asset_type_name: "",
        method_name: "",
        rs_name: "",
        created_by_id: 0,
        updated_by_id: 0,
        is_deleted: false,
        mileage_bands: 0,
        rs_rates: ["0"],
      },
      [],
      [1],
      [10],
      0,
      "",
    ],
    isPending
  );

  // functions
  const handleAdd = () => {
    setFormError(null);
    setLow([...low, low[counter] + 10]);
    setHigh([...high, high[counter] + 10]);
    setCounter(counter + 1);
  };
  const handleClose = () => {
    setCounter(1);
    setAssetRateSheetTrigger(false);
    setFormError(null);
    setRateSheet({
      id: 0,
      owner_company_id: currentCompany.id,
      method_id: 0,
      type_id: 0,
      asset_type_id: 0,
      type_name: "",
      asset_type_name: "",
      method_name: "",
      rs_name: "",
      created_by_id: 0,
      updated_by_id: 0,
      is_deleted: false,
      mileage_bands: 0,
      rs_rates: ["0"],
    });
    setRates([]);
    setLow([1]);
    setHigh([10]);
    setCounter(0);
    setRates([]);
    props.setDocId("");
  };

  const handleDelete = (e) => {
    setCounter(counter - 1);
    let i = parseInt(e.target.id);
    let arr = rates;
    if (arr[i]) {
      arr.splice(i, 1);
    }
    setRates(arr);
  };

  const handleChange = (e) => {
    let value = e.target.value;
    let name = e.target.name;
    setRateSheet({
      ...rateSheet,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsPending(true);
    setFormError(null);
    if (exists) {
      toggleNewMessage("This rate sheet already exists");
      setIsPending(false);
      return;
    } else if (counter === 0 && rateSheet.type_name === "Tier Rates") {
      toggleNewMessage("Please enter at least one mileage band 😅");
      setIsPending(false);
      return;
    } else if (
      rateSheet.method_name === "" ||
      rateSheet.type_name === "" ||
      rateSheet.asset_type_name === ""
    ) {
      toggleNewMessage(
        "Please make sure and select a hualing method, type, and asset type😅"
      );
      setIsPending(false);
      return;
    } else if (rates.type_name === "Tier Rates" && rates.length !== counter) {
      toggleNewMessage(
        "Please make sure and fill out all of the rates for each mileage band."
      );
      setIsPending(false);
      return;
    } else if (rates.type_name === "Fixed Rates" && rates.length === 0) {
      toggleNewMessage("Please add a rate for the Fixed Rate");
      setIsPending(false);
      return;
    }
    let method = "POST";
    if (id > 0) {
      method = "PATCH";
    }
    const requestBody = rateSheet;
    requestBody.created_by_id = currentUser.id;
    requestBody.updated_by_id = currentUser.id;
    requestBody.rs_rates = rates;
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const requestOptions = {
      body: JSON.stringify(requestBody),
      method,
      headers: headers,
    };

    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/asset-rate-sheet/${id}`,
      requestOptions
    )
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            setAuthStatus(false);
            setIsPending(false);
            setAssetRateSheetTrigger(false);
            navigate("/login");
          } else {
            toggleNewMessage("Something Went Wrong!");
            setIsPending(false);
            return;
          }
        } else {
          setAssetRateSheetTrigger(false);
          if (id > 0) {
            toggleNewMessage(null, "Rate sheet updated");
          } else {
            toggleNewMessage(null, "Rate sheet added");
          }
          setIsPending(false);
          props.setDocId("");
        }
      })
      .catch((_) => {
        toggleNewMessage("Something Went Wrong!");
        setIsPending(false);
        return;
      })
      .finally(() => {
        setIsPending(false);
      });
    return () => controller.abort();
  };

  return !dataPending ? (
    <div className="popup">
      <div className="ratesheet-popup-inner">
        {!isPending && (
          <div>
            <img
              className="close"
              onClick={handleClose}
              src={Close}
              alt="close img"
            />
          </div>
        )}
        <div className="mb-3 info">
          <h4>Add Rate Sheet</h4>
          {rateSheet.type_name === "Tier Rates" && (
            <button className="btn btn-secondary add" onClick={handleAdd}>
              Add Mileage Band
            </button>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-3 info">
            <InputField
              title={"Name"}
              className={"info-label"}
              type={"text"}
              name={"rs_name"}
              value={rateSheet.rs_name}
              onChange={handleChange}
              placeholder={"Enter Name"}
              required={true}
            />
            <label>
              <span>Hauling Method</span>
              <Select
                id="method_id"
                name="method_name"
                options={methodOptions}
                onChange={(option) => {
                  setRateSheet({
                    ...rateSheet,
                    method_id: option.value,
                    method_name: option.label,
                  });
                  setRates([]);
                }}
                placeholder={rateSheet.method_name}
                className="dropdown"
              />
            </label>
            <label>
              <span>Hauling Type</span>
              <Select
                id="type_id"
                name="type_name"
                options={typeOptions}
                onChange={(option) => {
                  setRateSheet({
                    ...rateSheet,
                    type_id: option.value,
                    type_name: option.label,
                  });
                  setRates([]);
                }}
                placeholder={rateSheet.type_name}
                className="dropdown"
              />
            </label>
            <label>
              <span>Asset Type</span>
              <Select
                id="asset_typ_id"
                name="asset_type_name"
                options={assetTypeOptions}
                onChange={(option) => {
                  setRateSheet({
                    ...rateSheet,
                    asset_type_id: option.value,
                    asset_type_name: option.label,
                  });
                  setRates([]);
                }}
                placeholder={rateSheet.asset_type_name}
                className="dropdown"
              />
            </label>
          </div>
          {rateSheet.type_name === "Fixed Rates" && (
            <div>
              <InputField
                title={"Rate"}
                className={"form-control"}
                type={"number"}
                step={"0.01"}
                name={"fixed_rate"}
                value={rates[0] || ""}
                onChange={(e) => {
                  let newArr = [...rates];
                  newArr[0] = parseFloat(e.target.value) || 0;
                  setRates(newArr);
                }}
                required={true}
              />
            </div>
          )}
          {rateSheet.type_name === "Tier Rates" && (
            <div className="container text-center">
              <div className="row">
                <div className="col-2 mileage-label">Start</div>
                <div className="col-2 mileage-label">End</div>
                <div className="col-2 rate-label">Rate</div>
              </div>
              {Array.from(Array(counter + 1)).map((c, index) => {
                return (
                  <div className="row" key={index}>
                    <div className="col-2 mileage">{low[index]}</div>
                    <div className="col-2 mileage">{high[index]}</div>
                    <div className="col rate">
                      <input
                        name={`mileage_rate${counter}`}
                        required
                        type="number"
                        step="0.01"
                        className="mileage-rate"
                        value={rates[index] || ""}
                        onChange={(e) => {
                          let newArr = [...rates];
                          newArr[index] = parseFloat(e.target.value) || 0;
                          setRates(newArr);
                        }}
                      />
                    </div>
                    <div className="col-2 mileage">
                      <img
                        id={index}
                        className="delete-band"
                        alt="delete img"
                        src={Delete}
                        onClick={handleDelete}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {formError && <p className="error">{formError}</p>}
          <FormButton pending={isPending} name={"Submit"} />
        </form>
      </div>
    </div>
  ) : (
    <Loading />
  );
}
